import { useState, useEffect, useRef, useCallback } from "react";
import { widgetSse } from "../../services/sse";
import { notificationStream } from "../../services/notifications";
import { chatApi } from "../../services/chatApi";
import MessageBubble, { groupActionNotes } from "../Chat/MessageBubble";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import {
  stripActions,
  extractArtifacts as extractArtifactsShared,
} from "../../utils/messageHelpers";
import {
  clipboardHasInlineText,
  extractClipboardFiles,
  getAttachmentContentType,
  mergeChatAttachments,
  readClipboardFiles,
  uploadChatAttachments,
} from "../../utils/chatAttachments";
import { collectPendingTaskIds } from "../../utils/chatPolling";
import "./ChatWidget.css";

/* ---------- inline icons (lucide-style) ---------- */
const Icon = ({ d, size = 18, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const IconChat = (p) => <Icon {...p} d={<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>} />;
const IconBot = (p) => <Icon {...p} d={<><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></>} />;
const IconUser = (p) => <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>} />;
const IconX = (p) => <Icon {...p} d={<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>} />;
const IconMax = (p) => <Icon {...p} d={<><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></>} />;
const IconMin = (p) => <Icon {...p} d={<><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></>} />;
const IconSend = (p) => <Icon {...p} d={<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>} />;
const IconStop = (p) => <Icon {...p} d={<><rect x="6" y="6" width="12" height="12" rx="2"/></>} />;
const IconChevron = (p) => <Icon {...p} d={<><polyline points="15 18 9 12 15 6"/></>} />;
const IconPlus = (p) => <Icon {...p} d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>} />;
const IconTrash = (p) => <Icon {...p} d={<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></>} />;
const IconRefresh = (p) => <Icon {...p} d={<><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15A9 9 0 0 1 5.64 18.36L1 14"/></>} />;

/* ---------- helpers ---------- */
// stripActions, extractArtifacts, formatBytes are imported from
// ../../utils/messageHelpers (shared with Chat.jsx).
const extractArtifactsFromHistoryItem = (m) => extractArtifactsShared(m?.actionsResults);

// Backend writes `datetime.utcnow().isoformat()` (no timezone suffix). JS
// `Date.parse` treats ISO strings without an offset as LOCAL time, which
// breaks freshness checks for users east of UTC. Append `Z` so the string
// is interpreted as UTC.
function parseUtc(iso) {
  if (!iso) return NaN;
  const s = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z";
  return Date.parse(s);
}

function timeAgo(iso) {
  if (!iso) return "";
  const ts = parseUtc(iso);
  if (Number.isNaN(ts)) return "";
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(ts).toLocaleDateString();
}

export default function ChatWidget() {
  const [state, setState] = useState("closed"); // closed | open | fullscreen
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState(() => chatApi.getCachedSessions());
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isAwaitingDetached, setIsAwaitingDetached] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Position (right/bottom offsets in px). null = use CSS default.
  const loadPos = (key) => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const [fabPos, setFabPos] = useState(() => loadPos("anna_widget_fab_pos"));
  const [panelPos, setPanelPos] = useState(() => loadPos("anna_widget_panel_pos"));
  const dragRef = useRef(null);

  const startDrag = (e, kind) => {
    if (state === "fullscreen") return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const initial = kind === "fab"
      ? (fabPos || { right: 24, bottom: 24 })
      : (panelPos || { right: 24, bottom: 24 });
    dragRef.current = { kind, startX, startY, initial, moved: false };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) + Math.abs(dy) > 3) dragRef.current.moved = true;
      const w = kind === "fab" ? 56 : (state === "fullscreen" ? 0 : (historyOpen ? 640 : 420));
      const h = kind === "fab" ? 56 : 600;
      const right = Math.max(8, Math.min(window.innerWidth - w - 8, initial.right - dx));
      const bottom = Math.max(8, Math.min(window.innerHeight - h - 8, initial.bottom - dy));
      const next = { right, bottom };
      if (kind === "fab") setFabPos(next); else setPanelPos(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Persist positions when they change
  useEffect(() => {
    if (fabPos) localStorage.setItem("anna_widget_fab_pos", JSON.stringify(fabPos));
  }, [fabPos]);
  useEffect(() => {
    if (panelPos) localStorage.setItem("anna_widget_panel_pos", JSON.stringify(panelPos));
  }, [panelPos]);

  const streamBufferRef = useRef("");
  const streamBuffersRef = useRef(new Map());
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldSnapRef = useRef(true);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const confirmTimerRef = useRef(null);
  const activeSessionRef = useRef(null);
  // Set to `true` whenever the widget needs to re-run the
  // "continue last active chat / start new chat" decision on the next
  // `sessions` reply. We re-arm this on every FAB open so the rule from
  // doc/frontend/conversations.md ("floating chat must take you to the most recently
  // active conversation when its updatedAt is < 1h ago, otherwise empty new
  // chat") is enforced every time the user opens the panel — not just the
  // first time after mount. Background `sessions` refreshes (e.g. after a
  // `done` event mid-conversation) do NOT re-arm the flag, so they cannot
  // yank the user into a different chat while they're already chatting.
  const pendingResolveRef = useRef(true);
  useEffect(() => {
    activeSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  /* ---- REST loaders (cache-first, stale-while-revalidate) ---- */
  const showActiveTurnPending = useCallback((message) => {
    setIsStreaming(false);
    setIsThinking(true);
    setIsAwaitingDetached(true);
    streamBufferRef.current = "";
    setMessages((prev) => {
      const withoutTransient = prev.filter((m) => !m.streaming && !m.detachedNote);
      return [
        ...withoutTransient,
        {
          role: "system",
          detachedNote: true,
          content: message || "Anna is still working — the reply will appear here when it's ready.",
          timestamp: new Date().toISOString(),
        },
      ];
    });
  }, []);

  const refreshActiveTurn = useCallback(async (sessionId) => {
    if (!sessionId) return false;
    try {
      const status = await widgetSse.activeTurn(sessionId);
      if ((status?.active || status?.pendingTasks) && activeSessionRef.current === sessionId) {
        showActiveTurnPending(status.pendingTasks && !status.active
          ? "Anna is waiting for a delegated task. The reply will appear here when it's ready."
          : undefined);
        return true;
      }
    } catch (err) {
      console.error("widget active turn check failed:", err);
    }
    return false;
  }, [showActiveTurnPending]);

  // Build the displayed message list from a /chat/history response.
  const applyHistory = useCallback((data) => {
    const hist = (data?.messages || []).flatMap((m) => {
      const mapped = {
        role: m.role,
        content: m.role === "assistant" ? stripActions(m.content) : m.content,
        timestamp: m.timestamp,
        kind: m.kind || "conversation",
        ...(m.requestId ? { requestId: m.requestId } : {}),
        ...(m.source ? { source: m.source } : {}),
        ...(m.metadata ? { metadata: m.metadata } : {}),
        ...(m.attachments?.length ? { attachments: m.attachments } : {}),
        ...(m.role === "assistant"
          ? (() => {
              const arts = extractArtifactsFromHistoryItem(m);
              return arts.length ? { artifacts: arts } : {};
            })()
          : {}),
      };
      // Reconstruct inline action_result notes from persisted actionsResults
      if (m.role === "assistant" && Array.isArray(m.actionsResults) && m.actionsResults.length) {
        const notes = m.actionsResults.map((a) => ({
          role: "system",
          actionNote: true,
          action: a.action || "unknown",
          success: a.success !== false,
          content: `Action: ${a.action || "unknown"} — ${a.success ? "Done" : "Failed"}`,
          ...(Array.isArray(a.images) && a.images.length ? { images: a.images } : {}),
          timestamp: m.timestamp,
        }));
        return [...notes, mapped];
      }
      return [mapped];
    });
    setMessages(hist);
    setIsAwaitingDetached(false);
    shouldSnapRef.current = true;
  }, []);

  const loadHistory = useCallback((sessionId) => {
    if (!sessionId) return;
    const cached = chatApi.getCachedHistory(sessionId);
    if (cached) applyHistory(cached);
    chatApi
      .fetchHistory(sessionId, { limit: 20 })
      .then((data) => {
        // Ignore late responses for a session the user already left.
        if (activeSessionRef.current === sessionId) {
          applyHistory(data);
          refreshActiveTurn(sessionId);
        }
      })
      .catch((err) => console.error("widget fetchHistory failed:", err));
  }, [applyHistory, refreshActiveTurn]);

  // Apply a session list, optionally re-running the continue-or-start rule.
  const applySessions = useCallback((list, { resolve = false } = {}) => {
    setSessions(list);

    // ── Continue-or-start rule (see doc/frontend/conversations.md) ──
    // Only run against authoritative (freshly fetched) data so an empty
    // cache on first load can't strand us on a blank "New Chat".
    if (resolve && pendingResolveRef.current) {
      pendingResolveRef.current = false;
      const latest = list[0];
      const latestTs = parseUtc(latest?.updatedAt);
      const fresh = !Number.isNaN(latestTs) && (Date.now() - latestTs) < 60 * 60 * 1000;
      if (fresh && latest?.id) {
        // Only switch if the freshest session differs from what we're
        // already showing — avoids reloading history mid-conversation.
        if (latest.id !== activeSessionRef.current) {
          setActiveSessionId(latest.id);
          setMessages([]);
          loadHistory(latest.id);
        }
      } else {
        setActiveSessionId(null);
        setMessages([]);
      }
    }
  }, [loadHistory]);

  const loadSessions = useCallback(() => {
    const cached = chatApi.getCachedSessions();
    if (cached.length) applySessions(cached, { resolve: false });
    chatApi
      .fetchSessions()
      .then((list) => applySessions(list, { resolve: true }))
      .catch((err) => console.error("widget fetchSessions failed:", err));
  }, [applySessions]);

  /* ---- streaming + notification frame dispatcher ---- */
  const handleFrame = useCallback((data) => {
    // ── Session-scoping guard ──
    // Discard streaming frames that belong to a different session than
    // the one currently displayed in the widget.
    const streamingTypes = new Set(["typing", "checking", "chunk", "done", "action_result", "stream_detached", "active_turn"]);
    if (streamingTypes.has(data.type)) {
      const frameSession = data._sseSessionId || data.sessionId;
      const currentSession = activeSessionRef.current;
      if (frameSession && currentSession && frameSession !== currentSession) {
        return;
      }
    }

    if (data.type === "typing" || data.type === "checking") {
        setIsThinking(true);
        setIsAwaitingDetached(false);
      } else if (data.type === "chunk") {
        setIsThinking(false);
        setIsAwaitingDetached(false);
        const previousContent = data.requestId
          ? streamBuffersRef.current.get(data.requestId) || ""
          : streamBufferRef.current;
        const rawContent = previousContent + (data.content || "");
        if (data.requestId) streamBuffersRef.current.set(data.requestId, rawContent);
        else streamBufferRef.current = rawContent;
        const display = stripActions(rawContent);
        setMessages((prev) => {
          const next = [...prev];
          const idx = next.findLastIndex((m) => m.streaming && (!data.requestId || m.requestId === data.requestId));
          if (idx >= 0) {
            next[idx] = { ...next[idx], content: display };
          } else {
            next.push({
              role: "assistant",
              content: display,
              timestamp: new Date().toISOString(),
              streaming: true,
              requestId: data.requestId,
              sessionId: data.sessionId,
            });
          }
          return next;
        });
      } else if (data.type === "done") {
        setIsStreaming(false);
        setIsThinking(false);
        setIsAwaitingDetached(false);
        const rawContent = data.requestId
          ? streamBuffersRef.current.get(data.requestId) || ""
          : streamBufferRef.current;
        const final = stripActions(rawContent);
        const artifacts = [];
        for (const a of data.actions || []) {
          if (a?.success && ["generate_html_file", "update_html_file", "get_html_download_link", "adopt_html_file"].includes(a?.action) && Array.isArray(a.artifacts)) {
            for (const art of a.artifacts) {
              if (art?.download_url) artifacts.push(art);
            }
          }
        }
        // Notify Notebook component if any page-related action succeeded
        const pageActions = ["attach_to_page", "update_page_section", "create_page", "append_to_page"];
        if ((data.actions || []).some((a) => a?.success && pageActions.includes(a?.action))) {
          window.dispatchEvent(new CustomEvent("anna-page-updated"));
        }
        setMessages((prev) => {
          const next = [...prev];
          const idx = next.findLastIndex((m) => m.streaming && (!data.requestId || m.requestId === data.requestId));
          if (idx >= 0) {
            next[idx] = {
              ...next[idx],
              content: final,
              streaming: false,
              ...(artifacts.length ? { artifacts } : {}),
            };
          } else if (artifacts.length) {
            next.push({
              role: "assistant",
              content: final,
              timestamp: new Date().toISOString(),
              artifacts,
            });
          }
          return next;
        });
        if (data.requestId) streamBuffersRef.current.delete(data.requestId);
        else streamBufferRef.current = "";
        loadSessions();
        if (collectPendingTaskIds(data.actions).length) {
          showActiveTurnPending("Anna is waiting for a delegated task. The reply will appear here when it's ready.");
        }
      } else if (data.type === "action_result") {
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            actionNote: true,
            action: data.action || "action",
            success: data.success !== false,
            content: data.message || `Action: ${data.action} — ${data.success ? "Done" : "Failed"}`,
            ...(Array.isArray(data.images) && data.images.length ? { images: data.images } : {}),
            timestamp: new Date().toISOString(),
          },
        ]);
      } else if (data.type === "stream_detached" || data.type === "active_turn") {
        // The HTTP stream died (tab backgrounded / network blip) but the
        // server keeps processing detached. Drop the streaming placeholder
        // and show a subtle note — the reply arrives via a `notification`
        // frame or a history reload on refocus.
        const sid = activeSessionRef.current;
        if (data.type === "active_turn" && data.active === false) {
          setIsStreaming(false);
          setIsThinking(false);
          setIsAwaitingDetached(false);
          if (sid) loadHistory(sid);
        } else {
          showActiveTurnPending(data.message);
        }
      } else if (data.type === "message") {
        setIsThinking(false);
        setIsAwaitingDetached(false);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.content || data.message, timestamp: new Date().toISOString() },
        ]);
      } else if (data.type === "notification") {
        // Unified ingestion frame from ChatService.inject_notification.
        // Append only when the floating widget is showing the same session.
        const incomingSid = data.sessionId;
        if (incomingSid && incomingSid === activeSessionRef.current) {
          setIsAwaitingDetached(false);
          setMessages((prev) => {
            const retained = prev.filter((m) => !m.detachedNote);
            const incoming = {
              role: "assistant",
              content: data.content || "",
              timestamp: data.timestamp || new Date().toISOString(),
              requestId: data.requestId,
              sessionId: incomingSid,
              kind: data.kind || "conversation",
              ...(data.source ? { source: data.source } : {}),
              ...(data.metadata ? { metadata: data.metadata } : {}),
            };
            const existing = data.requestId
              ? retained.findIndex((message) => message.role === "assistant" && message.requestId === data.requestId)
              : -1;
            if (existing < 0) return [...retained, incoming];
            const updated = [...retained];
            updated[existing] = { ...updated[existing], ...incoming, streaming: false };
            return updated;
          });
          void refreshActiveTurn(incomingSid);
        }
        loadSessions();
      } else if (data.type === "error") {
        setIsStreaming(false);
        setIsThinking(false);
        setIsAwaitingDetached(false);
        setMessages((prev) => [
          ...prev,
          { role: "system", content: `Error: ${data.message || "Unknown error"}`, timestamp: new Date().toISOString() },
        ]);
      }
  }, [loadSessions, loadHistory, refreshActiveTurn, showActiveTurnPending]);

  /* ---- streaming + notifications lifecycle ---- */
  useEffect(() => {
    const token = localStorage.getItem("anna_token");
    if (!token) return;

    // Chat streams from cloud; proactive notifications still arrive from the on-box service.
    widgetSse.onMessage = handleFrame;
    widgetSse.setToken(token);
    notificationStream.connect(token);
    const unsubscribeNotifications = notificationStream.subscribe(handleFrame);

    // Instant-load: paint the cached sidebar synchronously, then revalidate
    // sessions (and, via the continue-or-start rule, the active history).
    loadSessions();

    // Refocus safety net: when the tab comes back to the foreground, reload
    // the sidebar + active history so replies that completed while we were
    // backgrounded (detached turns, notifications missed during a dropped
    // EventSource) show up even if the push frame never arrived.
    const onResume = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (widgetSse._streamingActive) return; // live stream — don't wipe it
      notificationStream.connect(localStorage.getItem("anna_token"));
      loadSessions();
      const sid = activeSessionRef.current;
      if (sid) {
        loadHistory(sid);
        refreshActiveTurn(sid);
      }
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);

    return () => {
      // Keep the notification stream alive across renders; only drop our handler.
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      unsubscribeNotifications();
      widgetSse.onMessage = null;
    };
  }, [handleFrame, loadSessions, loadHistory, refreshActiveTurn]);

  useEffect(() => {
    if (!isAwaitingDetached || !activeSessionId) return;
    let cancelled = false;
    let timer;
    const startedAt = Date.now();
    const poll = async () => {
      if (Date.now() - startedAt >= 15 * 60 * 1000) {
        setIsAwaitingDetached(false);
        setIsThinking(false);
        setMessages((prev) => prev.map((message) => message.detachedNote
          ? { ...message, content: "Anna is taking longer than expected. Refresh to check the reply." }
          : message));
        return;
      }
      try {
        const status = await widgetSse.activeTurn(activeSessionId);
        if (cancelled || activeSessionRef.current !== activeSessionId) return;
        if (!status.active || status.pendingTasks) {
          const history = await chatApi.fetchHistory(activeSessionId, { limit: 20 });
          if (cancelled || activeSessionRef.current !== activeSessionId) return;
          applyHistory(history);
          if (status.active || status.pendingTasks) {
            showActiveTurnPending(status.pendingTasks && !status.active
              ? "Anna is waiting for a delegated task. The reply will appear here when it's ready."
              : undefined);
          } else {
            loadSessions();
          }
        }
        if (status.active || status.pendingTasks) timer = window.setTimeout(poll, 3000);
      } catch (err) {
        console.error("widget cloud turn check failed:", err);
        if (!cancelled) timer = window.setTimeout(poll, 3000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeSessionId, isAwaitingDetached, applyHistory, loadSessions, showActiveTurnPending]);

  /* ---- auto-scroll ---- */
  useEffect(() => {
    const scrollToEnd = () => {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    if (shouldSnapRef.current) {
      scrollToEnd();
      const t = setTimeout(scrollToEnd, 120);
      return () => clearTimeout(t);
    }
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom < 120) {
      scrollToEnd();
    }
  }, [messages, isStreaming, isThinking]);

  /* ---- textarea autogrow ---- */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  /* ---- delete confirmation auto-cancel ---- */
  useEffect(() => {
    if (!confirmDeleteId) return;
    confirmTimerRef.current = setTimeout(() => setConfirmDeleteId(null), 3000);
    return () => clearTimeout(confirmTimerRef.current);
  }, [confirmDeleteId]);

  /* ---- handlers ---- */
  const handleNewChat = () => {
    setActiveSessionId(null);
    shouldSnapRef.current = true;
    setMessages([]);
    streamBufferRef.current = "";
    streamBuffersRef.current.clear();
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    setAttachedFiles([]);
  };

  const handleRefresh = () => {
    // Re-pull the session list and reload the active session's messages.
    streamBufferRef.current = "";
    streamBuffersRef.current.clear();
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    loadSessions();
    const sid = activeSessionRef.current;
    if (sid) {
      setMessages([]);
      loadHistory(sid);
    }
  };

  const handleSelectSession = (id) => {
    setActiveSessionId(id);
    shouldSnapRef.current = true;
    setMessages([]);
    streamBufferRef.current = "";
    streamBuffersRef.current.clear();
    setIsAwaitingDetached(false);
    loadHistory(id);
  };

  const handleDeleteSession = (e, id) => {
    e.stopPropagation();
    if (confirmDeleteId === id) {
      chatApi.deleteSession(id).catch((err) =>
        console.error("widget deleteSession failed:", err)
      );
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (activeSessionId === id) handleNewChat();
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(id);
    }
  };

  const addFiles = useCallback((files) => {
    setAttachedFiles((prev) => mergeChatAttachments(prev, files));
  }, []);

  const handlePaste = useCallback(async (e) => {
    const pastedFiles = extractClipboardFiles(e.clipboardData);
    if (pastedFiles.length) {
      e.preventDefault();
      addFiles(pastedFiles);
      return;
    }
    if (clipboardHasInlineText(e.clipboardData)) {
      return;
    }
    const asyncFiles = await readClipboardFiles();
    if (!asyncFiles.length) return;
    e.preventDefault();
    addFiles(asyncFiles);
  }, [addFiles]);

  const handleEmojiSelect = useCallback((emoji) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? input.length;
    const end = textarea?.selectionEnd ?? start;
    const next = `${input.slice(0, start)}${emoji}${input.slice(end)}`;
    setInput(next);
    requestAnimationFrame(() => {
      if (!textarea) return;
      const caret = start + emoji.length;
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    });
  }, [input]);

  const handleSend = useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if ((!content && attachedFiles.length === 0) || isStreaming || isAwaitingDetached || isUploading) return;
      if (!widgetSse.isConfigured) {
        setMessages((prev) => [
          ...prev,
          { role: "system", content: "Anna cloud chat is not configured.", timestamp: new Date().toISOString() },
        ]);
        return;
      }
      const sid = activeSessionRef.current || `session_${Date.now()}`;
      if (!activeSessionRef.current) setActiveSessionId(sid);

      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content,
          timestamp: new Date().toISOString(),
          attachments: attachedFiles.map((file) => ({
            filename: file.name,
            contentType: getAttachmentContentType(file),
            size: file.size,
          })),
        },
      ]);
      shouldSnapRef.current = true;
      setInput("");
      setIsStreaming(true);
      setIsThinking(true);
      setIsAwaitingDetached(false);
      streamBufferRef.current = "";

      let attachmentsMeta;
      if (attachedFiles.length > 0) {
        setIsUploading(true);
        try {
          attachmentsMeta = await uploadChatAttachments(attachedFiles);
        } catch (err) {
          console.error("Widget file upload failed:", err);
          setMessages((prev) => [
            ...prev,
            { role: "system", content: `File upload failed: ${err.message}`, timestamp: new Date().toISOString() },
          ]);
          setIsUploading(false);
          setIsStreaming(false);
          setIsThinking(false);
          setIsAwaitingDetached(false);
          return;
        }
        setIsUploading(false);
      }

      setAttachedFiles([]);

      widgetSse.send(content, sid, attachmentsMeta).then((ok) => {
        if (!ok) {
          setIsStreaming(false);
          setIsThinking(false);
        } else {
          loadSessions();
        }
      });
    },
    [attachedFiles, input, isStreaming, isAwaitingDetached, isUploading, loadSessions]
  );

  const handleStop = () => {
    widgetSse.stop();
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.streaming) next[next.length - 1] = { ...last, streaming: false };
      return next;
    });
    streamBufferRef.current = "";
    streamBuffersRef.current.clear();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ---- render: closed = FAB ---- */
  if (state === "closed") {
    const fabStyle = fabPos ? { right: `${fabPos.right}px`, bottom: `${fabPos.bottom}px` } : undefined;
    return (
      <button
        className="anna-widget-fab"
        style={fabStyle}
        onMouseDown={(e) => startDrag(e, "fab")}
        onClick={() => {
          if (dragRef.current?.moved) { dragRef.current.moved = false; return; }
          // Re-arm the continue-or-start rule and refresh sessions so the
          // panel always opens on the most recently active conversation
          // (or an empty new chat if none is < 1h fresh).
          pendingResolveRef.current = true;
          loadSessions();
          setState("open");
        }}
        aria-label="Open chat"
        title="Drag to move — click to open"
      >
        <IconChat size={24} />
      </button>
    );
  }

  const showEmpty = messages.length === 0 && !isThinking;
  const panelStyle = state !== "fullscreen" && panelPos
    ? { right: `${panelPos.right}px`, bottom: `${panelPos.bottom}px` }
    : undefined;

  return (
    <div className={`anna-widget-panel ${state === "fullscreen" ? "fullscreen" : ""}`} style={panelStyle}>
      {/* History panel */}
      <aside className={`anna-widget-history ${historyOpen ? "open" : ""}`}>
        <div className="anna-widget-history-header">
          <span>History</span>
          <button className="anna-widget-iconbtn" onClick={handleNewChat} title="New chat">
            <IconPlus size={16} />
          </button>
        </div>
        <div className="anna-widget-history-list">
          {sessions.length === 0 && (
            <p className="anna-widget-history-empty">No conversations yet</p>
          )}
          {sessions.filter((s) => s.id).map((s) => (
            <div
              key={s.id}
              className={`anna-widget-history-item ${s.id === activeSessionId ? "active" : ""}`}
              onClick={() => handleSelectSession(s.id)}
              role="button"
              tabIndex={0}
            >
              <IconChat size={14} />
              <div className="anna-widget-history-meta">
                <div className="anna-widget-history-title" title={s.preview || "New conversation"}>
                  {s.preview || "New conversation"}
                </div>
                <div className="anna-widget-history-time">{timeAgo(s.updatedAt)}</div>
              </div>
              {confirmDeleteId === s.id ? (
                <button
                  className="anna-widget-iconbtn danger"
                  onClick={(e) => handleDeleteSession(e, s.id)}
                  title="Confirm delete"
                >
                  Delete?
                </button>
              ) : (
                <button
                  className="anna-widget-iconbtn"
                  onClick={(e) => handleDeleteSession(e, s.id)}
                  title="Delete"
                >
                  <IconTrash size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      </aside>

      {/* Main */}
      <div className="anna-widget-main">
        <header
          className="anna-widget-header"
          onMouseDown={(e) => {
            // Only drag when grabbing empty header area, not the buttons
            if (e.target.closest("button")) return;
            startDrag(e, "panel");
          }}
          style={{ cursor: state === "fullscreen" ? "default" : "move" }}
        >
          <button
            className="anna-widget-iconbtn"
            onClick={() => setHistoryOpen((v) => !v)}
            title={historyOpen ? "Hide history" : "Show history"}
          >
            <IconChevron size={16} />
          </button>
          <IconBot size={18} />
          <span className="anna-widget-title">{activeSessionId ? "Chat" : "New Chat"}</span>
          <button
            className="anna-widget-iconbtn"
            onClick={handleRefresh}
            title="Refresh"
          >
            <IconRefresh size={16} />
          </button>
          <button
            className="anna-widget-iconbtn"
            onClick={() => setState(state === "fullscreen" ? "open" : "fullscreen")}
            title={state === "fullscreen" ? "Restore" : "Maximise"}
          >
            {state === "fullscreen" ? <IconMin size={16} /> : <IconMax size={16} />}
          </button>
          <button
            className="anna-widget-iconbtn"
            onClick={() => { setState("closed"); setHistoryOpen(false); }}
            title="Close"
          >
            <IconX size={16} />
          </button>
        </header>

        <div className="anna-widget-messages" ref={messagesContainerRef} onScroll={() => {
          const el = messagesContainerRef.current;
          if (!el) return;
          const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
          if (dist > 200) shouldSnapRef.current = false;
          else if (dist < 50) shouldSnapRef.current = true;
        }}>
          {showEmpty && (
            <div className="anna-widget-empty">
              <h3>Anna</h3>
            </div>
          )}

          {groupActionNotes(
            messages
              .filter((m) => !(m.role === "user" && m.source === "routine"))
              .filter((m) => m.source !== "email_triage")
          ).map((m, i) => (
            <MessageBubble
              key={i}
              m={m}
              variant="widget"
              renderAvatar={(role) =>
                role === "user" ? <IconUser size={14} /> : <IconBot size={14} />
              }
            />
          ))}

          {isThinking && !messages.some((m) => m.streaming) && (
            <div className="anna-widget-msg assistant">
              <div className="anna-widget-avatar"><IconBot size={14} /></div>
              <div className="anna-widget-bubble">
                <span className="anna-widget-dots"><span /><span /><span /></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="anna-widget-input">
          {attachedFiles.length > 0 && (
            <div className="anna-widget-attachment-strip">
              {attachedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="attachment-chip">
                  {getAttachmentContentType(file).startsWith("image/") ? (
                    <img src={URL.createObjectURL(file)} alt={file.name} className="attachment-chip-thumb" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                  )}
                  <span className="attachment-chip-name">{file.name}</span>
                  <button type="button" className="attachment-chip-remove" onClick={() => setAttachedFiles((prev) => prev.filter((_, i) => i !== index))}>×</button>
                </div>
              ))}
            </div>
          )}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,text/x-python,text/javascript,text/typescript,text/html,text/css,application/json,application/xml,text/xml,text/yaml,application/x-yaml,.py,.js,.jsx,.ts,.tsx,.html,.htm,.css,.json,.xml,.yaml,.yml,.sql,.sh,.ino,.cpp,.cc,.cxx,.c,.h,.hpp,.swift,.java,.go,.rs"
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
          <div className="anna-widget-input-row">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={handlePaste}
              placeholder={isUploading ? "Uploading files..." : isAwaitingDetached ? "Waiting for Anna to finish..." : "Message Anna…"}
              rows={1}
              disabled={isStreaming || isAwaitingDetached}
            />
            <EmojiPicker
              onSelect={handleEmojiSelect}
              disabled={isStreaming || isAwaitingDetached}
            />
            <button
              className="anna-widget-toolbtn"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              disabled={isStreaming || isAwaitingDetached || isUploading}
              title="Attach files"
              aria-label="Attach files"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            {isStreaming ? (
              <button
                className="anna-widget-send anna-widget-stop"
                onClick={handleStop}
                title="Stop"
                aria-label="Stop"
              >
                <IconStop size={24} />
              </button>
            ) : (
              <button
                className="anna-widget-send"
                onClick={() => handleSend()}
                disabled={isAwaitingDetached || (!input.trim() && attachedFiles.length === 0)}
                title="Send"
              >
                <IconSend size={28} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
